import type { Theme, WidgetPlugin } from '@/common/enums';
import { Locale } from '@/i18n/locales';
import type { DictationSoundVariant } from '@/lib/audio/dictation-sounds';
import type { VoiceProvider } from '@/lib/dictation/types';
import type { SuggestionTemplate } from '@/lib/dynamic-suggestions/types';
import type { GuestAuthConfig } from '@/lib/guest-auth/types';
import type { TtsVoiceSettings } from '@/lib/tts/types';
import type { DataLayerBridgeConfig } from '@/lib/types/datalayer';
import type { WidgetDisplayConfigBase } from '@/lib/types/widget';
import { isReadableOnLight } from '@/lib/utils/color';
import type { SensitiveDataProtectionConfig } from '@/lib/utils/sensitive-data-detector';

import type { TenantExperiment } from './experiment';

/**
 * Plugin configuration with bundling options.
 *
 * Loading modes:
 * - `bundle: true` - Full plugin bundled in main widget script
 * - `bundleDetector: true` - Only detector bundled, full plugin lazy loaded when needed
 * - Neither - Plugin loaded via separate HTTP request at runtime (default)
 *
 * Note: `bundle` and `bundleDetector` are mutually exclusive.
 */
export interface PluginConfig {
  plugin: WidgetPlugin;
  /** When true, full plugin is bundled with widget code (loaded at build time). */
  bundle?: boolean;
  /** When true, only the detector is bundled; full plugin is lazy loaded when detector triggers it. */
  bundleDetector?: boolean;
}

/**
 * Entry in `iframeNavigation.targetDomains`. Either a plain pattern string
 * (backwards compatible) or an object pairing a pattern with an optional
 * initial locale to apply when the widget is embedded on that host.
 */
export type TargetDomainEntry = string | { pattern: string; locale?: Locale };

export interface LinkQueryParamRule {
  /** URL pattern(s) to match. Supports wildcards (*) for flexible matching. Can be a single pattern or array of patterns. */
  pattern: string | string[];
  /** Query parameters to append when any pattern matches */
  params: Record<string, string>;
}

export interface LinkQueryParamsConfig {
  /** Array of rules defining which query params to add based on URL patterns */
  rules: LinkQueryParamRule[];
}

export interface AffiliateConfig {
  /** Query parameter name to look for in incoming URLs. Defaults to 'ref'. */
  paramName?: string;
  /** Prefixes that already mark an ID as ours, so it is propagated as-is rather than prefixed. */
  validPrefixes?: string[];
  /** Prefix written onto IDs that carry none of ours. Defaults to 'bazak_'. */
  prefix?: string;
  /** Target parameter name for outgoing links. Defaults to paramName. */
  targetParam?: string;
  /** URL patterns where affiliate param should be applied (uses same pattern syntax as linkQueryParams) */
  applyToPatterns?: string[];
  /** Default affiliate ID for standalone app mode (non-embed). If set, used when no URL param or session-stored value exists. */
  standaloneAffiliateId?: string;
}

export interface TenantBrandingColors {
  /** Primary brand color (e.g., '#0f3766'). Also used as text color unless `foreground` is set. */
  primary?: string;
  /** Override body text color independently of primary brand color (e.g., '#000000'). */
  foreground?: string;
  /** Contrast color for text on primary backgrounds (e.g., '#ffffff') */
  primaryForeground?: string;
  /** Accent color for highlights/CTAs (e.g., '#e67134') */
  accent?: string;
  /** Contrast color for text on accent backgrounds */
  accentForeground?: string;
  /** Apply accent color to UI elements (suggestions hover, message bubbles) */
  applyAccentToUI?: boolean;
  /** Override brand-accent independently (carousel pagination, active indicators). Falls back to accent. */
  brandAccent?: string;
  /** Contrast color for text on brand-accent backgrounds. Falls back to accentForeground. */
  brandAccentForeground?: string;
  /** Warning/attention color (e.g., rating badges) */
  warning?: string;
  /** Informational color (e.g., board type badges) */
  info?: string;
  /** Destructive/error color (e.g., notes, alerts) */
  destructive?: string;
  /**
   * Dark mode overrides for **branded component tokens** (currently only
   * `--hotel-card-*` text/divider/strikethrough). Body text, surface, and
   * border colors derive from the host (ChatGPT/Claude inject canonical
   * `--color-*` vars per the MCP Apps spec) — these dark fields do NOT
   * drive `--color-text-primary`, `--color-background-*`, or
   * `--color-border-primary`.
   */
  dark?: {
    /** Text color used on branded card backgrounds in dark mode (default: '#f1f5f9'). Drives `--hotel-card-board-type-text` only when the tenant sets the corresponding light value. */
    foreground?: string;
    /** Strikethrough color in dark mode (default: 'rgba(255, 255, 255, 0.5)'). Drives `--hotel-card-strikethrough` only when the tenant sets `strikethroughColor`. */
    mutedForeground?: string;
    /** Divider color in dark mode (default: '#333'). Drives `--hotel-card-divider` only when the tenant sets `dividerColor`. */
    border?: string;
  };
}

export interface TenantBrandingFonts {
  /** Font for current locale, falls back to default */
  [locale: string]: string | undefined;
  /** Default font when locale-specific font is not defined */
  default?: string;
}

export interface TenantWidgetFont {
  /** Locale whose configured brand font the widget self-hosts. */
  locale: string;
}

export interface TenantIconConfig {
  /** Icon/logo path relative to /public/tenants/{tenantKey}/ (e.g., 'logo.png', 'icon.svg') */
  path?: string;
  /** Icon/logo width in pixels (default: 120 for sidebar, 32 for toolUI) */
  width?: number;
  /** Icon/logo height in pixels (auto by default) */
  height?: number;
}

export interface TenantSidebarTitleConfig {
  /** Color for sidebar title text (defaults to inherit) */
  color?: string;
  /** Font size in pixels for sidebar title (defaults to 14 / text-sm) */
  fontSize?: number;
  /** Font weight for sidebar title (defaults to 600 / font-semibold) */
  fontWeight?: number;
  /** When true, the sidebar title text is hidden (useful when the logo already contains the brand name) */
  hidden?: boolean;
}

export interface TenantSidebarStyle {
  icon?: TenantIconConfig;
  /** Icon shown when sidebar is collapsed (falls back to main icon) */
  collapsedIcon?: TenantIconConfig;
  title?: TenantSidebarTitleConfig;
  /** Image config for breadcrumb icon next to the page title */
  breadcrumbIcon?: TenantIconConfig;
}

export type WelcomeAlignment = 'start' | 'center' | 'end';

export interface TenantWelcomeImageConfig {
  /** Image path relative to /public/tenants/{tenantKey}/ */
  path: string;
  /** Inline CSS styles for the image (e.g., { width: 'clamp(60px, 15%, 120px)', maxHeight: '80px' }) */
  style?: Record<string, string>;
  /** Horizontal alignment of the image. Default: 'center' */
  align?: WelcomeAlignment;
  /** Alt text for accessibility */
  alt?: string;
}

export interface WelcomeTextStyle {
  /** Font size in pixels (e.g., 20) */
  fontSize?: number;
  /** Font weight (e.g., 400, 600, 700) */
  fontWeight?: number;
  /** Text color (hex, CSS variable, etc.) */
  color?: string;
}

export interface TenantWelcomeStyle {
  /** Image/icon displayed below the subtitle */
  image?: TenantWelcomeImageConfig;
  /** Horizontal alignment of the welcome section content (title, subtitle, image) */
  align?: WelcomeAlignment;
  /** Vertical alignment of the welcome section */
  verticalAlign?: WelcomeAlignment;
  /** Title text style overrides */
  title?: WelcomeTextStyle;
  /** Subtitle text style overrides */
  subtitle?: WelcomeTextStyle;
}

/**
 * Overrides for the carousel arrows. Every field is optional — the default is already a pale
 * disc with a brand-coloured glyph and a hairline ring, so most tenants need nothing here.
 */
export interface TenantCarouselButtonStyle {
  /** Disc fill. Default `var(--primary-foreground)`. */
  background?: string;
  /** Glyph colour. Default `var(--primary)`. */
  foreground?: string;
  /** Ring colour. Default `var(--border)`, matching the cards. */
  border?: string;
  /** Box shadow. Default lifts the disc off the cards; `none` for a flat surface. */
  shadow?: string;
  /**
   * Hover fill / glyph. Defaults to the tenant's `colors.accent` pair, and without an accent to
   * swapping the arrow's own two colours. Set these when the brand's carousel hover differs from
   * its accent — t5's site inverts to primary even though its accent is orange.
   */
  hoverBackground?: string;
  hoverForeground?: string;
  /** Ring colour on hover. Defaults to the hover fill, so the ring disappears into the disc. */
  hoverBorder?: string;
  /** Icon type for navigation arrows (defaults to 'chevron') */
  iconType?: 'chevron' | 'arrow';
  /**
   * `solid` (default) hugs the edge like a hairline border. `dashed` insets the ring a few px
   * for a stitched look — t5's, and the reason the ring is an outline rather than a border.
   */
  borderStyle?: 'solid' | 'dashed';
  /**
   * Show the arrows on touch devices too. Off by default — swiping already covers it. Applies to
   * the app surface; widget arrays are always swipe-only on touch.
   */
  showOnTouch?: boolean;
}

export interface TenantToolUIStyle {
  /** Icon for tool UI cards (e.g., flight cards) */
  icon?: TenantIconConfig;
}

export interface TenantAvatarConfig {
  /** Background color (e.g., '#3B82F6' or 'var(--primary)') */
  background?: string;
  /** Foreground/icon color (e.g., '#ffffff' or 'var(--primary-foreground)') */
  foreground?: string;
  /** Lucide icon name (e.g., 'User', 'Bot', 'Sparkles') */
  icon?: string;
  /** Image path relative to tenant assets or absolute URL */
  image?: string;
  /** Size in pixels (default: 32) */
  size?: number;
  /** Border radius in pixels (default: size/2 for circle) */
  borderRadius?: number;
}

export interface TenantMessageAvatarsConfig {
  /** Enable message avatars */
  enabled?: boolean;
  /** Gap between avatar and message in rem (default: 0.75) */
  gap?: number;
  /** Keep avatar visible while scrolling (default: false) */
  sticky?: boolean;
  /** User message avatar configuration */
  user?: TenantAvatarConfig;
  /** Assistant message avatar configuration */
  assistant?: TenantAvatarConfig;
}

export interface TenantComposerSendStyle {
  /** Background color (e.g., 'var(--brand-accent)') */
  background?: string;
  /** Text/icon color (e.g., 'var(--brand-accent-foreground)') */
  foreground?: string;
}

export interface TenantHotelCardStyle {
  /** CTA button background color */
  ctaBackground?: string;
  /** CTA button text color */
  ctaForeground?: string;
  /** Strikethrough (original) price color */
  strikethroughColor?: string;
  /** Board type badge background color */
  boardTypeBadgeBg?: string;
  /** Board type badge text color */
  boardTypeBadgeText?: string;
  /** Rating badge background color */
  ratingBadgeBg?: string;
  /** Rating badge text color */
  ratingBadgeText?: string;
  /** Divider color in alternative items */
  dividerColor?: string;
  /** Note/error text color */
  noteColor?: string;
  /** Border radius for filter chips and selectors (e.g., '4px') */
  filterChipRadius?: string;
}

/** Colour props that map one-to-one onto a CSS var; `styles/carousel-nav.css` derives the rest. */
const CAROUSEL_BUTTON_CSS_VAR_MAP = {
  background: '--carousel-btn',
  foreground: '--carousel-btn-foreground',
  border: '--carousel-btn-border',
  shadow: '--carousel-btn-shadow',
  hoverBackground: '--carousel-btn-hover',
  hoverForeground: '--carousel-btn-hover-foreground',
  hoverBorder: '--carousel-btn-hover-border',
} as const satisfies Partial<Record<keyof TenantCarouselButtonStyle, string>>;

/** Inset applied to the dashed ring so it reads as stitching rather than a border. */
const DASHED_RING_OFFSET = '-3px';

/**
 * Per-tenant carousel-arrow CSS vars, consumed by BrandingProvider.
 *
 * Only genuine deltas are emitted. The disc, the ring tint and the whole hover state are derived
 * from the glyph by `styles/carousel-nav.css`, so a tenant's palette lives in exactly one place.
 */
export function buildCarouselButtonVars(
  branding: Pick<TenantBranding, 'colors' | 'components'>,
): Record<string, string> {
  const { colors, components } = branding;
  const button = components?.carouselButton;
  const vars: Record<string, string> = {};

  for (const [prop, cssVar] of Object.entries(CAROUSEL_BUTTON_CSS_VAR_MAP)) {
    const value = button?.[prop as keyof typeof CAROUSEL_BUTTON_CSS_VAR_MAP];
    if (value) vars[cssVar] = value;
  }

  // The brand accent is the tenant's signature colour, while `primary` is usually a near-black
  // text navy — so the accent makes the more recognisably branded arrow. Plenty of accents are
  // pale tints though, which would leave the glyph unreadable, hence the contrast gate; those
  // tenants fall through to the stylesheet's `--primary` default.
  const brand = colors?.brandAccent ?? colors?.accent;
  if (!button?.foreground && brand && isReadableOnLight(brand)) {
    vars['--carousel-btn-foreground'] = brand;
  }

  if (button?.borderStyle === 'dashed') {
    vars['--carousel-btn-border-style'] = 'dashed';
    vars['--carousel-btn-border-offset'] = DASHED_RING_OFFSET;
  }

  // A CSS var rather than a React branch, so the touch policy lives beside the rest of the
  // arrow's styling instead of being split between JS and CSS.
  if (button?.showOnTouch) vars['--carousel-nav-touch-display'] = 'flex';

  return vars;
}

export const HOTEL_CARD_CSS_VAR_MAP: Record<keyof TenantHotelCardStyle, string> = {
  ctaBackground: '--hotel-card-cta-bg',
  ctaForeground: '--hotel-card-cta-fg',
  strikethroughColor: '--hotel-card-strikethrough',
  boardTypeBadgeBg: '--hotel-card-board-type-bg',
  boardTypeBadgeText: '--hotel-card-board-type-text',
  ratingBadgeBg: '--hotel-card-rating-bg',
  ratingBadgeText: '--hotel-card-rating-text',
  dividerColor: '--hotel-card-divider',
  noteColor: '--hotel-card-note',
  filterChipRadius: '--hotel-filter-chip-radius',
};

const DEFAULT_DARK_FOREGROUND = '#f1f5f9';
const DEFAULT_DARK_MUTED_FOREGROUND = 'rgba(255, 255, 255, 0.5)';
const DEFAULT_DARK_BORDER = '#333';

/**
 * Hotel-card token overrides for dark mode. Shared by the main-app branding provider and the
 * MCP-widget tenant theme so both render identically. Values fall back to spec defaults when the
 * tenant doesn't define them in `colors.dark`.
 *
 * Only ink that sits on a surface which itself darkens flips here. The rating badge is painted in
 * a fixed brand colour — a near-opaque yellow, for T4 — that looks the same in either theme,
 * so flipping its text to the dark foreground would leave near-white on yellow.
 */
export function buildHotelCardDarkOverrides(
  branding: TenantBranding | undefined,
): Record<string, string> {
  const hc = branding?.components?.hotelCard;
  if (!hc) return {};
  const fg = branding?.colors?.dark?.foreground ?? DEFAULT_DARK_FOREGROUND;
  const mutedFg = branding?.colors?.dark?.mutedForeground ?? DEFAULT_DARK_MUTED_FOREGROUND;
  const border = branding?.colors?.dark?.border ?? DEFAULT_DARK_BORDER;
  const vars: Record<string, string> = {};
  if (hc.boardTypeBadgeText) vars['--hotel-card-board-type-text'] = fg;
  if (hc.strikethroughColor) vars['--hotel-card-strikethrough'] = mutedFg;
  if (hc.dividerColor) vars['--hotel-card-divider'] = border;
  return vars;
}

export interface TenantBrandingComponents {
  carouselButton?: TenantCarouselButtonStyle;
  sidebar?: TenantSidebarStyle;
  toolUI?: TenantToolUIStyle;
  welcome?: TenantWelcomeStyle;
  /** Message avatar bubbles configuration */
  messageAvatars?: TenantMessageAvatarsConfig;
  /** Composer send button style overrides */
  composerSend?: TenantComposerSendStyle;
  /** Hotel card style overrides */
  hotelCard?: TenantHotelCardStyle;
}

export interface TenantBranding {
  colors?: TenantBrandingColors;
  fonts?: TenantBrandingFonts;
  /** Opts the widget into self-hosting one configured brand font. */
  widgetFont?: TenantWidgetFont;
  components?: TenantBrandingComponents;
}

export type ChatSuggestion = {
  title: string;
  label?: string;
  action?: string;
  icon?: string;
  /**
   * Time-box this suggestion to one ABSOLUTE window. ISO-8601, and the offset must be stated
   * (`2026-08-16T00:00:00+03:00`) — a bare local time resolves against the server's zone, not the
   * audience's. For RECURRING windows see `ScheduleCondition`, which does not apply here.
   */
  from?: string;
  /** Exclusive: the suggestion is gone from this instant on. */
  until?: string;
  /** Server-derived by `applySuggestionWindows`, never authored. Held out of the welcome shuffle. */
  pinned?: boolean;
};

/**
 * Day classification used by `ScheduleCondition.dayTypes`:
 * - `regular` — not a holiday and not a holiday-eve.
 * - `holiday` — today is a public holiday in the configured country.
 * - `holiday-eve` — tomorrow is a public holiday.
 */
export type DayType = 'regular' | 'holiday' | 'holiday-eve';

/** A RECURRING window. For a one-off absolute date, see `from`/`until` on `ChatSuggestion`. */
export interface ScheduleCondition {
  /** 0=Sunday … 6=Saturday (matches JS `Date.getDay`). Omit to match any day. */
  daysOfWeek?: readonly number[];
  /** "HH:mm" inclusive. Omit to treat as 00:00. */
  startTime?: string;
  /** "HH:mm" exclusive. Omit to treat as 24:00. If `startTime > endTime` the range wraps midnight. */
  endTime?: string;
  /** IANA timezone for this condition. Falls back to footer.timezone, then meta.timezone, then DEFAULT_TIMEZONE. */
  timezone?: string;
  /** Restrict the match to specific day classifications. Requires `country` on the footer config. Omit to ignore classification. */
  dayTypes?: readonly DayType[];
}

export interface ToolUIFooterRule {
  /**
   * Condition(s) for showing this rule. Accepts a single condition or an
   * array — array semantics are any-of (the rule matches if ANY condition
   * matches). Omit for a default/fallback rule (always matches).
   */
  when?: ScheduleCondition | readonly ScheduleCondition[];
  /** Markdown content. Supports links via the project's MarkdownLink. */
  text: string;
}

export type ToolUIFooterVisibility = 'results-and-empty' | 'results-only' | 'always';

export interface ToolUIFooterConfig {
  /** IANA timezone applied to all rules unless overridden by `when.timezone`. */
  timezone?: string;
  /**
   * ISO 3166-1 alpha-2 country code (e.g. `'IL'`). When set, the hook
   * dynamic-imports `date-holidays` and classifies today as
   * `regular` / `holiday` / `holiday-eve` for use with `ScheduleCondition.dayTypes`.
   * Only holidays whose type is in `holidayTypes` count.
   */
  country?: string;
  /**
   * `date-holidays` holiday categories that should count as a holiday for
   * classification. Defaults to `['public']`. Common values include
   * `'public'`, `'bank'`, `'school'`, `'optional'`, `'observance'`.
   */
  holidayTypes?: readonly string[];
  /** When to render the footer. Defaults to `'results-and-empty'`. */
  visibility?: ToolUIFooterVisibility;
  /** Ordered rules — first matching rule wins. */
  rules: readonly ToolUIFooterRule[];
  /** Optional extra class applied to the footer container. */
  className?: string;
  /** Tailwind text-size class to apply to the footer. Defaults to 'sm'. */
  fontSize?: ToolUIFooterFontSize;
}

export type ToolUIFooterFontSize = 'xs' | 'sm' | 'base' | 'lg';

export interface LocalizableContent {
  meta?: {
    title?: string;
    description?: string;
    tenantName?: string;
  };

  navigation?: {
    sidebarTitle?: string;
    sidebarLink?: string;
    termsUrl?: string;
  };

  chat?: {
    welcomeTitle?: string;
    welcomeSubtitle?: string;
    composerInputPlaceholder?: string;
    composerInputPlaceholderAfterFirstMessage?: string;
    /** Display name of the in-product assistant (e.g. "T4 AI", "AirPlan"). Interpolated into `thinkingIndicatorText`'s `{name}` placeholder. */
    assistantName?: string;
    /** Template shown in the thinking indicator and the activity trail's first row. Supports the `{name}` placeholder, resolved from `assistantName`. Falls back to `thinkingIndicator.default` translation when unset. */
    thinkingIndicatorText?: string;
    suggestions?: ChatSuggestion[];
    /**
     * Optional per-locale **copy** overrides for the activity trail. The on/off
     * toggle is NOT here — it lives in the non-localized top-level
     * `TenantConfig.activityTrail` (enabling the trail is a tenant decision,
     * not a per-language one). Tenant-specific final copy normally lives in the
     * trail next-intl namespace (`bucketCatalogNamespace`); these overrides are
     * an escape hatch for inlining variants in config.
     */
    activityTrail?: {
      preparingText?: string | readonly string[];
      streamingText?: string | readonly string[];
      longWaitText?: string | readonly string[];
      /**
       * Per-bucket overrides for the **final** receipt copy only. Intermediate
       * gap / streaming rows are bucket-agnostic by design — only the closer
       * is bucket-aware because it summarizes work that's already happened.
       */
      streamingDoneByBucket?: Record<string, readonly string[]>;
      /**
       * Tenant-specific final-row labels keyed by application-defined names.
       * Read by the tenant tool-group's `resolveDoneLabel` to compose multi-
       * tool / empty-results / count-aware closers (e.g. T5's flights ×
       * vacations matrix) without polluting shared LoadingUI namespaces.
       */
      doneLabels?: Record<string, string>;
    };
    /**
     * Per-tool-UI footer text rendered below the carousel. Keyed by MCP tool
     * name (e.g. `searchProducts`). Each footer can branch on day-of-week +
     * time-of-day in an IANA timezone, with locale-specific text. First
     * matching rule wins.
     */
    toolUIFooters?: Record<string, ToolUIFooterConfig>;
  };

  widget?: {
    tooltipTitle?: string;
    tooltipText?: string;
    tooltipIconSvg?: string;
    /** Pill launcher copy. Character limits are enforced at bake time. */
    launcher?: {
      welcome?: { title?: string; body?: string };
      examples?: string[];
    };
  };
}

type TranslationMap = Partial<Record<Locale, LocalizableContent>>;

export interface EnvironmentUrlOverrides {
  /** Base URL for this environment (e.g., 'https://t5pre.webguruetravel.com'). Also used for navigation.sidebarLink. */
  baseUrl: string;
}

export interface EnvironmentConfig {
  /** Pattern to match parentUrl hostname for environment detection (e.g., 'webguruetravel.com') */
  urlPattern?: string;
  /** Domains allowed to embed the widget in this environment. Defaults to [urlPattern] if not provided. */
  allowedEmbeddingDomains?: string[];
  /** Percentage of users who should see the widget (0-100). Defaults to 100 if not provided. */
  exposurePercentage?: number;
  /** URL overrides for this environment */
  urlOverrides?: EnvironmentUrlOverrides;
}

export interface TenantEnvironments {
  /** Production environment configuration (urlPattern is optional, serves as default) */
  production: EnvironmentConfig;
  /** Additional environments keyed by name (e.g., 'dev', 'staging') */
  [key: string]: EnvironmentConfig;
}

export interface DynamicSuggestionsConfig {
  /** When true, dynamic suggestions are fetched and cached for this tenant */
  enabled: boolean;
}

export interface GA4ProviderConfig {
  provider: 'ga4';
  /** GA4 Measurement ID (e.g., 'G-XXXXXXXXXX') — used for client-side gtag.js */
  measurementId: string;
  /** @server-only GA4 Measurement Protocol API secret name in Secret Manager */
  apiSecretName?: string;
}

export type AnalyticsProviderConfig = GA4ProviderConfig;

export interface EventForwardingRule {
  /** Subset of providers to send to (default: all configured providers) */
  providers?: string[];
  /** Rename the event when forwarding */
  eventName?: string;
  /** Only include these properties (whitelist) */
  includeProperties?: string[];
  /** Exclude these properties (blacklist, applied after include) */
  excludeProperties?: string[];
  /** Rename properties (internal name → external name) */
  renameProperties?: Record<string, string>;
}

export interface AnalyticsForwardingConfig {
  providers: AnalyticsProviderConfig[];
  events: Record<string, EventForwardingRule>;
}

/** Whitelist approach: only explicitly safe fields reach the client */
export type ClientSafeAnalyticsProviderConfig = Pick<
  GA4ProviderConfig,
  'provider' | 'measurementId'
>;

export interface ClientSafeAnalyticsForwardingConfig {
  providers: ClientSafeAnalyticsProviderConfig[];
  events: Record<string, EventForwardingRule>;
}

export interface TenantSeoConfig {
  /**
   * Allow search engines to index this tenant's root page.
   * Only "/" is indexable; all other paths are always blocked.
   * Default: false (noindex everything)
   */
  allowIndexing?: boolean;

  /**
   * Canonical domain for this tenant (without protocol).
   * Used when tenant has multiple domains pointing to same content.
   * All pages will include <link rel="canonical"> pointing to this domain.
   * Example: 'airplan.t5.co.il'
   */
  canonicalDomain?: string;
}

export interface McpDocsExample {
  prompt: string;
  /** Wire name of the tool the prompt exercises, e.g. 'searchFlights'. */
  uses?: string;
}

/**
 * Copy for the tenant's public MCP connector documentation page, which the marketing site renders
 * at `bazak.ai/mcp/<tenant>` and we submit to AI directories (Anthropic, OpenAI).
 *
 * Deliberately English-only and not localized: the audience is directory reviewers, not the
 * tenant's own customers.
 *
 * The tool list, descriptions, and parameter tables come from the live MCP server, and the
 * endpoint URL, website, and privacy-policy link are derived, so this block is the whole
 * per-tenant surface - adding a connector means adding it and nothing else.
 */
export interface TenantMcpDocsConfig {
  /** English name of the connector, e.g. 'T4 Hotels'. */
  displayName: string;
  /** One paragraph under the title: what the server exposes, in plain language. */
  lead: string;
  /** Set alone when every tool needs an account; pair with `authTools` for lazy auth. */
  auth?: boolean;
  /**
   * Wire names of the tools needing sign-in; implies `auth`. Config rather than derived because MCP
   * has no per-tool auth annotation and our gateway emits none.
   */
  authTools?: string[];
  /** e.g. 'mailto:support@example.com'. Falls back to Bazak support. */
  supportUrl?: string;
  /** Anthropic's directory review requires at least three. */
  examples: McpDocsExample[];
}

export interface SttConfig {
  enabled?: boolean;
  /** STT provider. Default: 'browser' (Web Speech API). */
  provider?: VoiceProvider;
  /** Enable sound feedback when dictation starts/stops. */
  enableSounds?: boolean;
  /**
   * Variant of the dictation sound effects to play. Defaults to the
   * production default in `DICTATION_SOUND_VARIANTS`. Unknown values fall
   * back to the default at runtime.
   */
  soundVariant?: DictationSoundVariant;
  /**
   * Custom endpoint for ElevenLabs Scribe token. Defaults to '/api/scribe-token'.
   * Useful when running outside the Next.js server (e.g., Storybook).
   */
  scribeTokenEndpoint?: string;
  /**
   * Domain-specific keyterms biasing for ElevenLabs Scribe (e.g. branded
   * product names, jargon, or homophones the model frequently misses). Merged
   * with the active locale's defaults from `lib/dictation/default-keyterms.ts`.
   * SDK limits: max 50 entries, ≤20 chars each.
   */
  keyterms?: readonly string[];
}

export interface TtsConfig {
  enabled?: boolean;
  /**
   * TTS provider. Currently `'elevenlabs'` is the only supported value and is
   * required for `enabled: true`. Reserved as a field for future providers.
   */
  provider?: 'elevenlabs';
  /** ElevenLabs voice ID. */
  voiceId?: string;
  /**
   * ElevenLabs model ID. Defaults to 'eleven_v3'.
   * Use 'eleven_flash_v2_5' for lower latency when quality is less critical.
   */
  modelId?: string;
  /**
   * ElevenLabs voice settings.
   * - stability: 0–1. Lower = more expressive/emotional. Higher = more consistent.
   * - similarityBoost: 0–1. How closely to match the original voice.
   * - style: 0–1. Style exaggeration — higher = more dramatic/expressive.
   * - useSpeakerBoost: Enhances voice clarity.
   */
  voiceSettings?: TtsVoiceSettings;
  /** Playback volume 0–1. Defaults to 0.65. */
  volume?: number;
}

export interface TenantConfig {
  api?: {
    allowedEndpoints?: string[];
  };

  /**
   * Publish the RFC 9728 protected-resource document for this tenant's `/mcp`. Only for
   * a backend that actually challenges — advertising one that never refuses anything
   * sends hosts into a sign-in the tools do not need.
   */
  mcpAuth?: boolean;
  guestAuth?: GuestAuthConfig;

  /**
   * Where thread history persists. `'local'` (default): IndexedDB via AssistantCloudLocal.
   * `'server'`: bazak-mastra's `/:tenant/threads*` endpoints via AssistantCloudServer —
   * IndexedDB is never opened. Required for resumable-streaming tenants.
   */
  persistence?: 'local' | 'server';

  /**
   * Opt-in resumable streaming: the chat transport persists the stream id
   * (`x-resumable-stream-id`) and replays interrupted streams from
   * `GET /:tenant/chat/resume/:streamId`.
   */
  resumableStreaming?: boolean;

  /**
   * Page gate for HITL + server persistence + resumable streaming: when set, those features are
   * active ONLY in widget mode on parent pages whose URL starts with an `include` prefix and no
   * `exclude` prefix (e.g. t19's logged-in customers portal, minus its login page). On any
   * other surface — the standalone app page, or the widget on non-matching pages — the tenant
   * behaves like a plain local-persistence tenant. `?debug` bypasses the gate for testing.
   * Omit to keep the features active everywhere.
   */
  hitlPages?: { include: string[]; exclude?: string[] };

  /**
   * SEO configuration for search engine indexing and canonical URLs.
   * By default, all tenants are noindex. Enable indexing explicitly for public tenants.
   */
  seo?: TenantSeoConfig;

  /** Copy for the public MCP connector documentation page. Omit for tenants with no MCP listing. */
  mcpDocs?: TenantMcpDocsConfig;

  security?: {
    allowedEmbeddingDomains?: string[];
    /** @server-only Sensitive data protection configuration */
    sensitiveDataProtection?: SensitiveDataProtectionConfig;
  };

  branding?: TenantBranding;

  /**
   * Agent configuration for AI model and behavior
   */
  agent?: {
    /**
     * Tool UI mapping - maps MCP tool names to UI component identifiers
     * Example: { search: 'search-t7-products', getProductDetails: 'hidden' }
     */
    toolUIs?: Record<string, string>;
  };

  /**
   * Activity-trail display options. The trail itself is always on for every
   * tenant (non-disableable) — this only carries display tweaks. Non-localized
   * on purpose. Per-locale trail *copy* lives in the tenant's trail next-intl
   * namespace (see a tool group's `bucketCatalogNamespace`) or, as an escape
   * hatch, in the localized `LocalizableContent.chat.activityTrail` overrides.
   */
  activityTrail?: {
    /** When false, the trail is hidden in read-only history views. */
    showInHistory?: boolean;
  };

  meta: {
    language: Locale;
    availableLocales?: readonly Locale[];
    enableLanguageSelector?: boolean;
    /** Speech-to-text (dictation) configuration. */
    stt?: SttConfig;
    /** Text-to-speech configuration. */
    tts?: TtsConfig;
    /**
     * Detect message language on first message and offer to switch.
     * Default: `true` when `navigation.iframeNavigation.targetDomains` contains
     * at least one entry with a `locale` override; otherwise `false`.
     */
    enableMessageLanguageDetection?: boolean;
    /**
     * How the hotel `PriceRow` component formats decimals. Other price-rendering
     * components currently format with their own defaults and ignore this flag.
     * - 'rounded' (default): always round to integer (e.g. 149.50 → 150)
     * - 'auto': show up to 2 decimals when fractional part is non-zero
     *   (e.g. 150.00 → 150, 149.50 → 149.50)
     */
    priceDecimals?: 'rounded' | 'auto';
    enableThemeToggle?: boolean;
    defaultTheme?: Theme;
    /**
     * Indicates if tenant has custom favicon/logo assets in /public/tenants/{tenant}/
     * Defaults to true. Set to false for tenants using default assets.
     */
    hasCustomAssets?: boolean;
    /** Favicon extension (default: 'png') */
    faviconExtension?: 'png' | 'ico';
    /** OG image extension for social sharing (default: 'png') */
    ogImageExtension?: 'png' | 'jpg';
    /**
     * Cross-tab sync timeout in seconds. When the last tab closes,
     * a timestamp is stored. If reopened after this timeout, all sync
     * data is reset. Default: 43200 (12 hours). Set to 0 to disable sync.
     */
    crossTabSyncTimeout?: number;
    /**
     * IANA timezone identifier used for date/time formatting.
     * Defaults to 'Asia/Jerusalem'. Must be consistent between server and client
     * renders — derive from config, not from the browser at render time.
     */
    timezone?: string;
    /**
     * When true, static suggestions are shuffled on first render and the selected order is
     * persisted in sessionStorage for the user's session. Dynamic suggestions (if any) are
     * merged server-side before the array reaches the client and are treated the same as
     * static ones for display purposes.
     */
    randomizeSuggestions?: boolean;
  };

  navigation: {
    /**
     * Base URL for the tenant's main website.
     * Used for:
     * - Resolving relative links in markdown content
     * - Redirect endpoint when token contains relative path
     * - Environment URL resolution (production vs dev)
     * Example: 'https://www.t5.co.il'
     */
    baseUrl?: string;
    /**
     * URL for sidebar header navigation. When clicked, navigates to this URL.
     * Defaults to baseUrl if not specified. Only set if different from baseUrl.
     */
    sidebarLink?: string;
    sidebarIcon?: string;
    termsUrl?: string;

    iframeNavigation?: {
      /**
       * Target domain entries. Each entry is either a plain pattern string, or
       * an object pairing a pattern with an optional locale override applied
       * when the widget is embedded on that host (see useHostLanguageDetection).
       * Patterns support `*.foo.com` (suffix wildcard), exact, and subdomain-of.
       */
      targetDomains: readonly TargetDomainEntry[];
    };

    /**
     * Rules for appending query parameters to links based on URL patterns.
     * Patterns support wildcards (*) for flexible matching.
     * Note: These rules are NOT applied to /redirect/ URLs (handled server-side).
     * @deprecated Remove after 2026-03-01 - Only needed for backward compatibility
     * with older conversations that don't use /redirect/ URLs.
     * New conversations use server-side redirect which handles query params in the token.
     */
    linkQueryParams?: LinkQueryParamsConfig;

    /**
     * Affiliate tracking configuration for capturing and passing affiliate IDs.
     * When configured, extracts affiliate ID from URL query params (e.g., ?ref=bzk_partner)
     * and applies it to outgoing links matching the specified patterns.
     */
    affiliateConfig?: AffiliateConfig;
  };

  widget?: {
    /** When true, customer cannot override widget config via window.BazakWidgetConfig */
    configLocked?: boolean;
    /**
     * Add a preconnect hint to the document head for the widget's base URL.
     * Improves loading performance by establishing early connections.
     * Default: true. Set to false to disable the preconnect link.
     */
    preconnect?: boolean;
    /** Widget display configuration - uses shared WidgetDisplayConfigBase from @/lib/types/widget */
    display?: WidgetDisplayConfigBase;
    /**
     * Plugins to load on the parent page (customer site).
     * Each plugin has a corresponding script in plugin-files/.
     * Lazy plugins are combined, minified, and served separately.
     * Bundled plugins are included in the main widget script.
     */
    plugins?: PluginConfig[];
    /**
     * Pages where the widget tooltip is hidden but the button remains visible.
     * Supports path patterns with wildcards (e.g., '/checkout/*', '/payment').
     * Also supports brace expansion for alternation (e.g., '/reservation/{deal,payment,order}/*').
     * On quiet pages: button is visible and clickable, but tooltip won't appear.
     */
    quietPages?: string[];
    /**
     * Pages where widget button is completely hidden but widget remains loaded.
     * Supports path patterns with wildcards and brace expansion.
     * On hidden pages: script loads, events sent, but button is NOT rendered
     * and chat cannot be opened.
     */
    hiddenPages?: string[];
    /**
     * When true, non-exposed users receive a minimal tracking-only stub (~800B)
     * that provides BazakWidget.track() and getDistinctId() without loading the full widget.
     * When false/undefined, non-exposed users get an empty `/* not exposed *​/` response.
     * Default: false.
     */
    enableTrackingStub?: boolean;

    /**
     * Funnel bridge: which of the tenant's own `dataLayer` events to mirror, and
     * how their URLs map to funnel steps. Requires
     * `WidgetPlugin.DataLayerBridge` in `plugins`. GA4 ecommerce events are
     * inherited from `GA4_ECOMMERCE_EVENTS`, so this usually only carries the
     * step map and the session key. See `docs/datalayer-bridge.md`.
     */
    dataLayer?: DataLayerBridgeConfig;
  };

  /**
   * Experiments this tenant runs, on any surface. Declared here rather than per-feature so
   * `listExperiments` can answer what is live, per tenant — see
   * `lib/experiments/registry.ts`.
   */
  experiments?: TenantExperiment[];

  /**
   * Multi-environment configuration. When defined, environment is detected
   * based on parentUrl matching urlPattern. Defaults to 'production' if no match.
   */
  environments?: TenantEnvironments;

  translations: TranslationMap;

  /** @server-only Locale-independent suggestion templates, resolved per-locale at SSR time */
  suggestionTemplates?: SuggestionTemplate[];

  /**
   * @server-only Dynamic suggestions configuration.
   * When enabled, suggestions are fetched from external sources, cached, and served via SSR.
   */
  dynamicSuggestions?: DynamicSuggestionsConfig;

  /**
   * Forward analytics events to external providers (e.g., tenant's Google Analytics).
   * Client-safe fields (measurementId, events) are sent to the client.
   * Server-only fields (apiSecretName) stay server-side for Measurement Protocol.
   */
  analyticsForwarding?: AnalyticsForwardingConfig;

  analytics?: {
    /** Enable PostHog session recordings. Default: true */
    enableSessionRecording?: boolean;
  };

  /**
   * Per-trigger chat content overrides. Keys are trigger prefixes.
   * When the widget opens with a matching trigger, the override's translations replace defaults.
   */
  triggerOverrides?: Record<string, { translations: TranslationMap }>;
}

type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

export type PartialTenantConfig = DeepPartial<TenantConfig>;

export type ConfigOverrides = {
  meta?: Partial<Pick<TenantConfig['meta'], 'language' | 'defaultTheme'>>;
};

/**
 * Whether a tenant's guests get the in-agent rooms flow rather than a link to the hotel's own page:
 * never, always, or whichever arm the experiment assigns them.
 *
 * One value rather than a pair of booleans, so "not available here" and "available and forced on"
 * can't be claimed at once.
 */
export type RoomSelectionMode = 'off' | 'on' | 'experiment';

export interface EnvironmentUrls {
  /** Production base URL (before environment overrides) */
  production: string;
  /** Current environment base URL (equals production if in production env) */
  current: string;
}

export type ClientSafeConfig = Pick<
  TenantConfig,
  'meta' | 'navigation' | 'translations' | 'branding' | 'triggerOverrides' | 'activityTrail'
> & {
  /** Resolved tenant key (e.g., 't5', 't10') - used for API routing */
  tenantKey: string;
  /** Base path for loading tenant assets (opaque, doesn't reveal tenant name) */
  assetBasePath?: string;
  /** Tool UI mapping - maps MCP tool names to UI component identifiers */
  toolUIs?: Record<string, string>;
  /** Numeric ID for the tool group component. Maps to imports server-side. */
  toolGroupId?: number;
  /** Multiple tool group IDs — used when rendering conversations from all tenants. */
  toolGroupIds?: number[];
  /** Numeric IDs of the tenant's HITL toolkits (human tools + approval UIs). */
  hitlToolkitIds?: number[];

  approvalLabelsNamespace?: string;
  /** Thread-history persistence mode (see TenantConfig.persistence). */
  persistence?: 'local' | 'server';
  /** Resumable-streaming opt-in (see TenantConfig.resumableStreaming). */
  resumableStreaming?: boolean;
  // Guest-authenticated tenants attach a bearer to each chat turn.
  mcpAuth?: boolean;
  guestAuth?: GuestAuthConfig;
  /**
   * True when the tenant's `hitlPages` gate suppressed HITL/server features on this page.
   * The transport forwards it so the backend also withholds the tenant's client HITL tools
   * (otherwise the model could call a tool no UI is registered for).
   */
  hitlSuppressed?: boolean;
  /** Translation namespaces needed for the current tenant's tool UIs (computed server-side) */
  toolUITranslationNamespaces?: string[];
  security?: {
    sensitiveDataProtection?: SensitiveDataProtectionConfig;
  };
  /** Base URLs for URL replacement (production vs current environment) */
  environmentUrls?: EnvironmentUrls;
  /**
   * Resolved feature flags, not tenant JSON: read server-side per request so the client never
   * evaluates a flag itself, and absent when nothing was resolved.
   */
  features?: {
    roomSelection?: RoomSelectionMode;
  };
  widget?: Pick<
    NonNullable<TenantConfig['widget']>,
    'display' | 'configLocked' | 'plugins' | 'quietPages' | 'enableTrackingStub'
  >;
  /** Client-safe analytics forwarding config (no API secrets) */
  analyticsForwarding?: ClientSafeAnalyticsForwardingConfig;
  analytics?: {
    enableSessionRecording?: boolean;
  };
  /**
   * Base URL for the bazak-assistant-ui API.
   * When set, internal API calls (e.g. /api/destinations) are prefixed with this URL.
   * Used when the package is consumed by an external app on a different origin.
   */
  apiBaseUrl?: string;
};
